import React, { useEffect, useState } from 'react';
import { notification } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getBrowserTimezone, getTimezoneOptions } from '../lib/timezones';
import { setPageMeta } from '../lib/seo';

const Register: React.FC = () => {
  const { register, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    timezone: getBrowserTimezone(),
  });
  const [acceptedPolicies, setAcceptedPolicies] = useState(false);

  const timezoneOptions = getTimezoneOptions();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    setPageMeta({
      title: 'Create Account | Clouniq',
      description: 'Create a clouniq account to deploy Cloud PC and Cloud VPS resources.',
    });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!acceptedPolicies) {
      notification.error({ message: 'Please accept the Terms and Privacy Policy' });
      return;
    }

    setLoading(true);
    try {
      await register(formData.firstName, formData.lastName, formData.email, formData.password, formData.timezone);
      notification.success({ message: 'Account created successfully' });
    } catch (error: any) {
      notification.error({ message: error?.response?.data?.error || 'Failed to register' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container auth-container-wide">
        <div className="auth-card glass auth-shell">
          <aside className="auth-aside">
            <span className="section-tag">New Workspace</span>
            <h2 className="auth-title">Create your account</h2>
            <p className="auth-subtitle">Set up your team access and start deploying compute sessions in minutes.</p>
            <ul className="auth-points">
              <li>Instant account activation</li>
              <li>Timezone-aware scheduling</li>
              <li>Single dashboard for sessions and billing</li>
            </ul>
          </aside>

          <form className="auth-form auth-form-panel" onSubmit={handleSubmit}>
            <h3>Get started</h3>
            <p className="auth-form-copy">Create your account to launch machines and manage billing.</p>

            <div className="form-row">
              <div className="auth-field">
                <label className="auth-label" htmlFor="register-first-name">First name</label>
                <input
                  id="register-first-name"
                  type="text"
                  placeholder="First name"
                  required
                  value={formData.firstName}
                  onChange={(e) => setFormData((prev) => ({ ...prev, firstName: e.target.value }))}
                />
              </div>
              <div className="auth-field">
                <label className="auth-label" htmlFor="register-last-name">Last name</label>
                <input
                  id="register-last-name"
                  type="text"
                  placeholder="Last name"
                  required
                  value={formData.lastName}
                  onChange={(e) => setFormData((prev) => ({ ...prev, lastName: e.target.value }))}
                />
              </div>
            </div>

            <div className="auth-field">
              <label className="auth-label" htmlFor="register-email">Email</label>
              <input
                id="register-email"
                type="email"
                placeholder="Work email"
                required
                value={formData.email}
                onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
              />
            </div>

            <div className="auth-field">
              <label className="auth-label" htmlFor="register-password">Password</label>
              <input
                id="register-password"
                type="password"
                placeholder="Password"
                required
                value={formData.password}
                onChange={(e) => setFormData((prev) => ({ ...prev, password: e.target.value }))}
              />
            </div>

            <div className="auth-field">
              <label className="auth-label" htmlFor="register-timezone">Timezone</label>
              <select
                id="register-timezone"
                value={formData.timezone}
                onChange={(e) => setFormData((prev) => ({ ...prev, timezone: e.target.value }))}
                required
              >
                {timezoneOptions.map((tz: string) => (
                  <option key={tz} value={tz}>
                    {tz}
                  </option>
                ))}
              </select>
            </div>

            <label className="auth-consent">
              <input
                type="checkbox"
                checked={acceptedPolicies}
                onChange={(e) => setAcceptedPolicies(e.target.checked)}
              />
              <span>
                By registering you agree to our{' '}
                <Link to="/terms">Terms of Service</Link> and{' '}
                <Link to="/privacy">Privacy Policy</Link>.
              </span>
            </label>

            <button type="submit" className="auth-btn primary" disabled={loading}>
              {loading ? 'Creating account...' : 'Create account'}
            </button>

            <p className="auth-switch">
              Already have an account? <Link to="/login">Sign in</Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Register;



